import { DownloadIcon } from '@chakra-ui/icons';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Box,
  DrawerFooter,
  Button,
  VStack,
  Image,
  // HStack,
  // Radio,
  Center,
  useToast,
  Portal,
  Checkbox,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Modal,
  useDisclosure,
  Icon,
  ModalBody,
  Textarea,
  FormControl,
  FormLabel,
  Divider,
} from '@chakra-ui/react';
import { Api } from 'api';
import { LoadingText } from 'components';
import { createCustomerSocket, getExpertiseBackroundImageUrl } from 'core';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  BodyPartAnswerInfo,
  BodyPartExpertiseInfo,
  CheckOldBodyDamagesResponse,
  ExpertiseBodyDamagePartType,
  ExpertiseBodyDamageType,
  ExpertiseCarBodyPartName,
  ExpertiseCarBodyTypes,
  VehicleQueryCacheKeys,
} from 'types';
import { Socket } from 'socket.io-client';
import { t, Trans } from '@lingui/macro';
import { ExpertiseBodyworkExpertiseStateSelector } from './ExpertiseBodyworkExpertiseStateSelector';

export interface ExpertiseBodyworkExpertiseDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  carBodyType: ExpertiseCarBodyTypes;
  isDisabled?: boolean;
}

type ExpertisePartsData = {
  [key: number]: ExpertiseBodyDamageType[];
};

type ExpertiseQuestionData = {
  [key: number]: string;
};

type ExpertiseSaveData = { expertises: ExpertisePartsData; answers: ExpertiseQuestionData; opinionNote: string };

type PositionLookupTableRow = {
  left?: string;
  bottom?: string;
  top?: string;
  right?: string;
};

type PositionLookupTable = {
  [key in ExpertiseCarBodyPartName]: PositionLookupTableRow;
};

const genericPositionLookupTable: PositionLookupTable = {
  [ExpertiseCarBodyPartName.SagOnSasi]: {
    top: '0%',
    right: '15%',
  },
  [ExpertiseCarBodyPartName.SagOnCamurluk]: {
    top: '20%',
    right: '13%',
  },
  [ExpertiseCarBodyPartName.SagOnKapi]: {
    top: '40%',
    right: '10%',
  },
  [ExpertiseCarBodyPartName.SagMarspiyel]: {
    top: '50%',
    right: '2%',
  },
  [ExpertiseCarBodyPartName.SagArkaCamurluk]: {
    top: '75%',
    right: '13%',
  },
  [ExpertiseCarBodyPartName.SagArkaKapi]: {
    top: '60%',
    right: '10%',
  },
  [ExpertiseCarBodyPartName.SagArkaSasi]: {
    bottom: '0%',
    right: '12%',
  },

  [ExpertiseCarBodyPartName.OnTampon]: {
    top: '2%',
    right: '45%',
  },
  [ExpertiseCarBodyPartName.OnPanel]: {
    top: '2%',
    right: '55%',
  },
  [ExpertiseCarBodyPartName.Kaput]: {
    top: '20%',
    right: '45%',
  },
  [ExpertiseCarBodyPartName.Tavan]: {
    top: '58%',
    right: '45%',
  },
  [ExpertiseCarBodyPartName.BagajKapagi]: {
    top: '80%',
    right: '45%',
  },
  [ExpertiseCarBodyPartName.ArkaTampon]: {
    top: '95%',
    right: '45%',
  },
  [ExpertiseCarBodyPartName.ArkaPanel]: {
    top: '95%',
    right: '55%',
  },

  [ExpertiseCarBodyPartName.SolOnSasi]: {
    top: '0%',
    left: '15%',
  },
  [ExpertiseCarBodyPartName.SolOnCamurluk]: {
    top: '20%',
    left: '13%',
  },
  [ExpertiseCarBodyPartName.SolOnKapi]: {
    top: '40%',
    left: '13%',
  },
  [ExpertiseCarBodyPartName.SolMarspiyel]: {
    top: '50%',
    left: '5%',
  },
  [ExpertiseCarBodyPartName.SolArkaCamurluk]: {
    top: '75%',
    left: '13%',
  },
  [ExpertiseCarBodyPartName.SolArkaKapi]: {
    top: '60%',
    left: '13%',
  },
  [ExpertiseCarBodyPartName.SolArkaSasi]: {
    bottom: '0%',
    left: '12%',
  },

  [ExpertiseCarBodyPartName.SolUstDirek]: {
    top: '30%',
    left: '27%',
  },
  [ExpertiseCarBodyPartName.SagUstDirek]: {
    top: '30%',
    right: '27%',
  },
};

const VehicleTypePositionTable: {
  [key in ExpertiseCarBodyTypes]: PositionLookupTable;
} = {
  [ExpertiseCarBodyTypes.Sedan]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Hatchback5]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Hatchback2]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Pickup]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Pickup2]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Minibus]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Panelvan]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.CanliPanelvan]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.TekSurguPanelvan]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.SUV]: genericPositionLookupTable,
  [ExpertiseCarBodyTypes.Station]: genericPositionLookupTable,
};

const DamagePriorityMap: Record<string, number> = {
  [ExpertiseBodyDamageType.Cutting]: 1,
  [ExpertiseBodyDamageType.Changed]: 2,
  [ExpertiseBodyDamageType.Damaged]: 3,
  [ExpertiseBodyDamageType.Processed]: 4,
  [ExpertiseBodyDamageType.Removed]: 5,
  [ExpertiseBodyDamageType.Painted]: 6,
  [ExpertiseBodyDamageType.HalfPainted]: 7,
  [ExpertiseBodyDamageType.LocallyPainted]: 8,
  [ExpertiseBodyDamageType.FoilCoated]: 9,
  [ExpertiseBodyDamageType.Plastic]: 10,
  [ExpertiseBodyDamageType.Original]: 11,
};

const sortDamageTypes = (types: ExpertiseBodyDamageType[]) => {
  return [...types].sort((a, b) => {
    const priorityA = DamagePriorityMap[a] || 99;
    const priorityB = DamagePriorityMap[b] || 99;
    return priorityA - priorityB;
  });
};

export const ExpertiseBodyworkExpertiseDrawer: React.FC<ExpertiseBodyworkExpertiseDrawerProps> = ({
  isOpen,
  onClose,
  carBodyType,
  isDisabled,
}) => {
  const customer = useSelector((selector: RootState) => selector.customer.focusedCustomer);
  const inputPositionTable = useMemo(() => VehicleTypePositionTable[carBodyType], [carBodyType]);
  const toast = useToast();
  const allPaintedRef = useRef<HTMLInputElement>(null);
  const [isAllPainted, setIsAllPainted] = useState(false);
  const [oldReportUrl, setOldReportUrl] = useState('');
  const {
    isOpen: checkOldDamagesModalIsOpen,
    onOpen: checkOldDamagesModalOnOpen,
    onClose: checkOldDamagesModalOnClose,
  } = useDisclosure();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customerSocket, setCustomerSocket] = useState<Socket | null>(null);
  const {
    data: expertises,
    isLoading: isExpertisesLoading,
    refetch: refetchExpertises,
  } = useQuery(
    [VehicleQueryCacheKeys.GetBodyWorkExpertises, customer!.id],
    async () => {
      const { data: expertiseResponse } = await Api.Expertise.getCustomerBodyPartsExpertise(
        customer!.id,
        ExpertiseBodyDamagePartType.Expertise,
      );
      const { expertiseBodyDamages, expertiseBodyAnswers } = expertiseResponse;

      const filteredExpertiseBodyDamages = expertiseBodyDamages.filter(
        (bodyDamage: BodyPartExpertiseInfo) =>
          bodyDamage!.expertiseBodyDamagePart!.vehicleType?.name === customer!.customerVehicle!.vehicleType?.name,
      );
      if (filteredExpertiseBodyDamages.length) {
        if (filteredExpertiseBodyDamages?.some((bodyDamage) => bodyDamage.expertiseBodyDamageValue !== 'B')) {
          setIsAllPainted(false);
        } else {
          setIsAllPainted(true);
        }
      } else {
        setIsAllPainted(false);
      }

      let opinionNoteData = null;
      try {
        const { data } = await Api.Expertise.getOpinionNote(customer!.id, ExpertiseBodyDamagePartType.Expertise);
        opinionNoteData = data;
      } catch {
        // Opinion note doesn't exist yet - use default
      }

      return {
        expertises: expertiseBodyDamages.reduce((accum: any, nextValue: BodyPartExpertiseInfo) => {
          // eslint-disable-next-line no-param-reassign
          accum[nextValue.expertiseBodyDamagePartId] = nextValue.expertiseBodyDamageValue
            ? (nextValue.expertiseBodyDamageValue.split(',') as ExpertiseBodyDamageType[])
            : [ExpertiseBodyDamageType.Original];
          return accum;
        }, {} as ExpertisePartsData),
        answers: expertiseBodyAnswers.reduce((accum: any, nextValue: BodyPartAnswerInfo) => {
          // eslint-disable-next-line no-param-reassign
          accum[nextValue.expertiseBodyQuestionId] = nextValue.answer;
          return accum;
        }, {} as ExpertiseQuestionData),
        opinionNote: opinionNoteData?.note || '',
      };
    },
    {
      refetchOnWindowFocus: false,
      enabled: customer != null && isOpen,
      cacheTime: 0,
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Boyalı Değişen verileri çekilirken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const { data: expertiseTemplates, isLoading: isExpertisesTemplateLoading } = useQuery(
    VehicleQueryCacheKeys.GetBodyWorkExpertiseTemplates,
    async () => {
      const response = await Api.Expertise.getExpertiseTemplate(
        customer!.customerVehicle!.vehicleTypeId,
        ExpertiseBodyDamagePartType.Expertise,
      );
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: customer != null && isOpen,
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Boyalı değişen işlemleri için gereken verileri çekerken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      answers: (expertises?.answers || {}) as ExpertiseQuestionData,
      expertises: (expertises?.expertises || {}) as ExpertisePartsData,
      opinionNote: (expertises?.opinionNote || '') as string,
    },
    onSubmit: async (data: ExpertiseSaveData) => {
      if (expertiseTemplates) {
        const filledExpertises = expertiseTemplates.expertiseBodyDamageParts.reduce((accum, next) => {
          // eslint-disable-next-line no-param-reassign
          accum[next.id] = data.expertises[next.id] || [ExpertiseBodyDamageType.Original];
          return accum;
        }, {} as any);

        const filledAnswers = expertiseTemplates.expertiseBodyQuestions.reduce((accum, next) => {
          // eslint-disable-next-line no-param-reassign
          accum[next.id] = data.answers[next.id] || '0';
          return accum;
        }, {} as any);

        if (checkOldDamagesModalIsOpen) {
          checkOldDamagesModalOnClose();
          // eslint-disable-next-line no-use-before-define
          saveExpertiseMutate.mutateAsync({
            expertises: filledExpertises,
            answers: filledAnswers,
            opinionNote: data.opinionNote,
          });
        } else {
          // eslint-disable-next-line no-use-before-define
          const oldResults = await checkOldDamageMutate.mutateAsync({
            expertises: filledExpertises,
            answers: filledAnswers,
            opinionNote: data.opinionNote,
          });
          if (!oldResults.hasInconsistency) {
            // eslint-disable-next-line no-use-before-define
            saveExpertiseMutate.mutateAsync({
              expertises: filledExpertises,
              answers: filledAnswers,
              opinionNote: data.opinionNote,
            });
          } else {
            setOldReportUrl(oldResults.oldReportUrl!);
            checkOldDamagesModalOnOpen();
          }
        }
      }
    },
  });
  const saveExpertiseMutate = useMutation<void, any, ExpertiseSaveData>(
    async (data: ExpertiseSaveData) => {
      await Api.Expertise.saveExpertiseBodyParts({
        customerId: customer!.id,
        expertiseBodyDamagePartTypeId: ExpertiseBodyDamagePartType.Expertise,
        bodyDamageAnswers: Object.entries(data.answers).map(([key, value]) => {
          return {
            answer: value,
            expertiseBodyQuestionId: parseInt(key, 10),
          };
        }),
        bodyDamageParts: Object.entries(data.expertises).map(([key, value]) => {
          const sortedValue = Array.isArray(value) ? sortDamageTypes(value) : value;
          return {
            bodyDamageValue: Array.isArray(sortedValue) ? sortedValue.join(',') : sortedValue,
            expertiseBodyDamagePartId: parseInt(key, 10),
          };
        }),
      });

      if (data.opinionNote !== undefined) {
        await Api.Expertise.saveOpinionNote(customer!.id, ExpertiseBodyDamagePartType.Expertise, data.opinionNote);
      }
    },
    {
      onSuccess: () => {
        onClose();
        toast({
          title: 'Başarı ile kayıt edildi',
          status: 'success',
          isClosable: true,
        });
      },
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Kayıt sırasında hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const checkOldDamageMutate = useMutation<CheckOldBodyDamagesResponse, any, ExpertiseSaveData>(
    async (data: ExpertiseSaveData) => {
      const response = await Api.Expertise.checkOldBodyDamages({
        customerId: customer!.id,
        expertiseBodyDamagePartTypeId: ExpertiseBodyDamagePartType.Expertise,
        bodyDamageAnswers: Object.entries(data.answers).map(([key, value]) => {
          return {
            answer: value,
            expertiseBodyQuestionId: parseInt(key, 10),
          };
        }),
        bodyDamageParts: Object.entries(data.expertises).map(([key, value]) => {
          const sortedValue = Array.isArray(value) ? sortDamageTypes(value) : value;
          return {
            bodyDamageValue: Array.isArray(sortedValue) ? sortedValue.join(',') : sortedValue,
            expertiseBodyDamagePartId: parseInt(key, 10),
          };
        }),
      });
      return response.data;
    },
    {
      onError: (err) => {
        console.error(err);
        toast({
          title: 'Kontrol sırasında hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );
  const { setFieldValue, values, handleSubmit, resetForm } = formik;
  const onBodyworkExpertiseChanged = (
    expertiseKeyName: ExpertiseCarBodyPartName,
    expertiseBodyworkExpertiseState: ExpertiseBodyDamageType,
  ) => {
    const currentValues = values.expertises[expertiseKeyName] || [ExpertiseBodyDamageType.Original];
    let newValues: ExpertiseBodyDamageType[] = [];

    if (expertiseBodyworkExpertiseState === ExpertiseBodyDamageType.Original) {
      newValues = [ExpertiseBodyDamageType.Original];
    } else {
      const withoutOriginal = currentValues.filter((v) => v !== ExpertiseBodyDamageType.Original);

      if (withoutOriginal.includes(expertiseBodyworkExpertiseState)) {
        newValues = withoutOriginal.filter((v) => v !== expertiseBodyworkExpertiseState);
      } else {
        newValues = [...withoutOriginal, expertiseBodyworkExpertiseState];
      }

      if (newValues.length === 0) {
        newValues = [ExpertiseBodyDamageType.Original];
      }
    }
    setFieldValue(`expertises.${expertiseKeyName}`, sortDamageTypes(newValues));
  };

  const selectAllPainted = () => {
    if (allPaintedRef!.current?.checked) {
      expertiseTemplates?.expertiseBodyDamageParts.forEach((bodyDamagePart) => {
        setFieldValue(`expertises.${bodyDamagePart.id}`, [ExpertiseBodyDamageType.Painted]);
      });
      setIsAllPainted(true);
    } else {
      resetForm();
      setIsAllPainted(false);
    }
  };

  const expertiseImageUrl = getExpertiseBackroundImageUrl(carBodyType);

  useEffect(() => {
    if (customer && isOpen) {
      const newCustomerSocket = createCustomerSocket(customer.id.toString());
      setCustomerSocket(newCustomerSocket);

      const handleExpertiseBodyDamageChanged = (data: any) => {
        if (data.customer.id === customer.id) {
          refetchExpertises();
        }
      };

      newCustomerSocket.on('expertiseBodyDamageChanged', handleExpertiseBodyDamageChanged);

      return () => {
        newCustomerSocket.off('expertiseBodyDamageChanged', handleExpertiseBodyDamageChanged);
        newCustomerSocket.disconnect();
      };
    }
    return () => {
      if (customerSocket) {
        customerSocket.disconnect();
      }
    };
  }, [customer, isOpen, refetchExpertises]);

  const showLoading = !customer || !expertiseTemplates || isExpertisesTemplateLoading || isExpertisesLoading || !isOpen;

  return (
    <Portal>
      <Modal isOpen={checkOldDamagesModalIsOpen} onClose={checkOldDamagesModalOnClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Uyarı</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Trans>Geçmiş ekspertiz raporuna göre boya değişenlerde değişiklik tespit edilmiştir.</Trans>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button
              leftIcon={<Icon as={DownloadIcon} />}
              colorScheme="teal"
              onClick={() => {
                window.open(oldReportUrl);
              }}
            >
              <Trans>Geçmiş Ekspertiz Raporu</Trans>
            </Button>
            <Button
              onClick={() => {
                handleSubmit();
              }}
            >
              <Trans>Kaydet</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Drawer isOpen={isOpen} onClose={onClose} size="xl">
        <DrawerOverlay />
        <DrawerContent overflow="auto">
          <form noValidate onSubmit={handleSubmit}>
            <DrawerCloseButton disabled={showLoading || saveExpertiseMutate.isLoading} />
            <DrawerHeader>
              <Trans>Kaporta Boya Değişen Kontrolü</Trans>
            </DrawerHeader>

            <DrawerBody>
              {showLoading ? (
                <Center>
                  <LoadingText />
                </Center>
              ) : (
                <VStack gap={24} align="stretch">
                  <VStack>
                    <Box width="100%" position="relative" maxWidth="450px">
                      <Image src={expertiseImageUrl} fit="fill" width="100%" />
                      <Box position="absolute" top="0" left="0" right="0" bottom="0">
                        {expertiseTemplates.expertiseBodyDamageParts.map((expertiseTemplate) => {
                          const partInfo = inputPositionTable[expertiseTemplate.displayId];
                          return (
                            <ExpertiseBodyworkExpertiseStateSelector
                              key={`ebw${expertiseTemplate.displayId}`}
                              expertiseKeyName={expertiseTemplate.id}
                              displayId={expertiseTemplate.displayId}
                              onBodyworkExpertiseChanged={onBodyworkExpertiseChanged}
                              value={values.expertises[expertiseTemplate.id] || [ExpertiseBodyDamageType.Original]}
                              left={partInfo.left}
                              top={partInfo.top}
                              bottom={partInfo.bottom}
                              right={partInfo.right}
                              label={expertiseTemplate.name}
                              isDisabled={isDisabled}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  </VStack>
                  <Box>
                    <Box mb={4}>
                      <Checkbox
                        ref={allPaintedRef}
                        colorScheme="blue"
                        onChange={selectAllPainted}
                        isChecked={isAllPainted}
                      >
                        <Trans>Tamamı Boyalı</Trans>
                      </Checkbox>
                    </Box>

                    {/* {expertiseTemplates.expertiseBodyQuestions.map((expertiseBodyQuestion) => (
                      <FormControlFormRadioGroup
                        key={`bweq${expertiseBodyQuestion.id}`}
                        label={expertiseBodyQuestion.question}
                        onChange={(value) => {
                          setFieldValue(`answers.${expertiseBodyQuestion.id}`, value);
                        }}
                        value={values.answers[expertiseBodyQuestion.id] || '0'}
                        isDisabled={isDisabled}
                      >
                        <HStack spacing="24px">
                          <Radio value="1">Evet</Radio>
                          <Radio value="0">Hayır</Radio>
                        </HStack>
                      </FormControlFormRadioGroup>
                    ))} */}
                  </Box>

                  <Divider />

                  <Box width="100%">
                    <FormControl>
                      <FormLabel fontWeight="bold">
                        <Trans>Kanaat Notu</Trans>
                      </FormLabel>
                      <Textarea
                        placeholder={t`Uzman görüşünüzü buraya yazınız...`}
                        value={values.opinionNote}
                        onChange={(e) => setFieldValue('opinionNote', e.target.value)}
                        rows={4}
                        resize="vertical"
                        maxLength={5000}
                        isDisabled={isDisabled}
                      />
                    </FormControl>
                  </Box>
                </VStack>
              )}
            </DrawerBody>

            <DrawerFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={onClose}
                disabled={showLoading || saveExpertiseMutate.isLoading}
              >
                <Trans>İptal</Trans>
              </Button>
              {!isDisabled && (
                <Button type="submit" disabled={showLoading || saveExpertiseMutate.isLoading}>
                  <Trans>Kaydet</Trans>
                </Button>
              )}
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>
    </Portal>
  );
};
